import {
  Alert,
  CircularProgress,
  Grid,
  InputBaseComponentProps,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { ComponentType, FC, useContext, useState } from 'react';

import { useUpdatePaymentPlan } from '@features/payments/hooks/useUpdatePaymentPlan';
import { useUpdateProfile } from '@features/profile/hooks/useUpdateProfile';

import StripeInput from '../StripeInput/StripeInput';
import { useUpdateEnterpricePaymentPlans } from '@features/payments/hooks/useUpdateEnterpricePaymentPlans';
import { toast } from 'react-toastify';
import { LoadingButton } from '@mui/lab';
import { useEnterpriseBoostSocial } from '@features/boostMyJobs/hooks/useEnterpriseBoostSocial';
import { useEnterpriseBoostLocation } from '@features/boostMyJobs/hooks/useEnterpriseBoostLocation';
import { useBoostLocation } from '@features/boostMyJobs/hooks/useBoostLocation';
import { useBoostSocial } from '@features/boostMyJobs/hooks/useBoostSocial';
import { useAddPaymentMethod } from '@features/payments/hooks/useAddPaymentMethod';
import { authContext } from '@staffedupusa/shared-components';
import { BillingCycle, PaymentPeriod, PlanType } from '@features/payments';
import { track } from '@amplitude/analytics-browser';
import { amplitudeEventsName } from '@utils/amplitudeEvents';
import { Plan } from '../PricingOptions/constants';
import { useUpdatePlan } from '@features/payments/hooks/useUpdatePlan';

type PaymentInformationFormProps = {
  selectedPlanId?: string | null;
  handleClose: () => void;
  handleSucess?: () => void;
  isEnterprise?: boolean;
  boostCharge?: boolean;
  selectedCompanyId?: string | number;
  locationBoost?: boolean;
  socialBoost?: boolean;
  numberOfWeeks?: number;
  companyId?: number;
  enterpriseCall?: boolean;
  addPaymentInformation?: boolean;
  halfWidth?: boolean;
  currentPaymentInfo?: boolean;
  paymentPeriod?: BillingCycle | PaymentPeriod;
  selectedPlan?: PlanType | Plan | null;
};

const PaymentInformationForm: FC<PaymentInformationFormProps> = ({
  selectedPlanId,
  handleClose,
  handleSucess,
  isEnterprise,
  boostCharge,
  selectedCompanyId,
  companyId,
  locationBoost,
  socialBoost,
  numberOfWeeks,
  addPaymentInformation,
  enterpriseCall,
  halfWidth,
  paymentPeriod,
  selectedPlan,
  currentPaymentInfo,
}) => {
  const stripe = useStripe();
  const elements = useElements();

  const { authData } = useContext(authContext);
  const currentPlan = authData?.user?.company?.plan;


  const [errorText, setErrorText] = useState<string>('');

  const { updateProfile } = useUpdateProfile({
    setErrorText,
  });

  const [isPaymentLoading, setIsPaymentLoading] = useState(false);

  const { updatePaymentPlan, isUpdatingPaymentPlan } = useUpdatePlan({
    setErrorText,
  });

  const { addPaymentMethod, isAddingPaymentMethod } = useAddPaymentMethod({
    setErrorText,
  });

  const { boostEnterpriseSocial, isBoostEnterpriseSocialLoading } =
    useEnterpriseBoostSocial({
      setErrorText,
    });

  const { boostEnterpriseLocation, isBoostEnterpriseLocationLoading } =
    useEnterpriseBoostLocation({
      setErrorText,
    });

  const { boostLocation, isBoostLocationLoading } = useBoostLocation({
    setErrorText,
  });

  const { boostSocial, isBoostSocialLoading } = useBoostSocial({
    setErrorText,
  });

  const { updateEnterprisePaymentPlan, isUpdatingEnterprisePaymentPlan } =
    useUpdateEnterpricePaymentPlans({
      onSuccess: () => {
        handleClose();
        toast.success('Payment Plan Updated');
      },
    });

  const onSubmit = async (e: any) => {
    e.preventDefault();

    const options = {
      onSuccess: (data: { status: number; message: any; }) => {
        if (data.status === 200) {
          toast.success('Boost Successful');
          setIsPaymentLoading(false);
          handleClose();
        } else{
          setErrorText(data?.message || 'Error occurred while boosting');
        }
      },
      onError: () => {
        setIsPaymentLoading(false);
      },
    };

    const subscriptionOptions = {
      onSuccess: (data: { status: number }) => {
        if (data.status === 200) {
          const amplitudeEvent = {
            eventType: amplitudeEventsName.SUBSCRIPTION_PLAN_SELECTED,
            eventProperties: {
              description:
                'User selected a subscription cadence and added their credit card.',
              ...(authData.user && { userId: authData.user?.id }),
              ...(selectedPlan && { productId: selectedPlan }),
              ...(paymentPeriod && { 'subscritoin-length': paymentPeriod }),
            },
          };
          updateProfile(
            {
              walkthrough_completed: true,
              has_payment_method: true,
            },
            {
              onSuccess: () => {
                halfWidth &&
                  track(
                    amplitudeEvent.eventType,
                    amplitudeEvent.eventProperties,
                  );
                toast.success('Payment Successful');
                setIsPaymentLoading(false);
                handleClose();
              },
            },
          );
        } else {
          setIsPaymentLoading(false);
        }
      },
      onError: () => {
        setIsPaymentLoading(false);
      },
    };
    if (!stripe || !elements) {
      return;
    }

    const cardElement = elements.getElement(CardNumberElement);
    if (cardElement) {
      const { token } = await stripe.createToken(cardElement);
      const addPaymentOptions = {
        onSuccess: async (data: { status: number }) => {
          if (data.status === 200) {
            if (handleSucess) {
              await handleSucess();
            }
            handleClose();
            setIsPaymentLoading(false);
          } else {
            setIsPaymentLoading(false);
          }
        },

        onError: () => {
          setIsPaymentLoading(false);
        },
      };
      if (token) {
        setErrorText('');
        setIsPaymentLoading(true);
      }
      if (token && isEnterprise) {
        updateEnterprisePaymentPlan({
          token: token.id as string,
          plan: selectedPlanId || '',
        });
      } else if (token && boostCharge) {
        if (socialBoost) {
          if (enterpriseCall) {
            boostEnterpriseSocial(
              { company_id: selectedCompanyId, token: token.id as string },
              options,
            );
          } else {
            boostSocial(
              {
                company_id: selectedCompanyId || undefined,
                token: token.id as string,
              },
              options,
            );
          }
        } else if (locationBoost && numberOfWeeks) {
          if (enterpriseCall) {
            boostEnterpriseLocation(
              {
                number_weeks: numberOfWeeks,
                company_id: selectedCompanyId,
                token: token.id as string,
              },
              options,
            );
          } else {
            boostLocation(
              { number_weeks: numberOfWeeks, token: token.id as string },
              options,
            );
          }
        }
      } else if (addPaymentInformation && token && companyId && !selectedPlan) {
        addPaymentMethod(
          {
            token: token.id as string,
            company_id: companyId,
          },
          addPaymentOptions,
        );
      } else if (addPaymentInformation && token && !selectedPlan) {
        addPaymentMethod(
          {
            token: token.id as string,
          },
          addPaymentOptions,
        );
      } else if (addPaymentInformation && token && selectedPlan) {
        const selectedPlanId = selectedPlan === "smarter" || selectedPlan === 'StaffedUp Service'
          ? 1
          : selectedPlan === "pro" || selectedPlan === 'Service + Onboarding'
            ? 2
            : 4;
        const selectedBillingCycle = paymentPeriod === 'Monthly'
          ? 1
          : paymentPeriod === 'Bi-Annual' || paymentPeriod === 'Semi-Annually'
            ? 2
            : 3
        updatePaymentPlan(
          {
            token: token.id as string,
            plan_ids: [
              {
                company_id: companyId,
                plan_id: currentPlan,
                new_plan_id: selectedPlanId,
              },
            ],
            billing_cycle: selectedBillingCycle,
          },
          subscriptionOptions,
        );
      }
    }
  };

  return (
    <form style={{ width: halfWidth ? '50%' : '100%' }} onSubmit={onSubmit}>
      <Stack sx={{ width: '100%' }} spacing={2}>
        {errorText && <Alert severity="error">{errorText}</Alert>}

        <Grid container spacing={2} rowGap={1}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              InputProps={{
                inputComponent:
                  StripeInput as ComponentType<InputBaseComponentProps>,
                inputProps: {
                  component: CardNumberElement,
                },
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              InputProps={{
                inputComponent:
                  StripeInput as ComponentType<InputBaseComponentProps>,
                inputProps: {
                  component: CardExpiryElement,
                },
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              size="small"
              InputProps={{
                inputComponent:
                  StripeInput as ComponentType<InputBaseComponentProps>,
                inputProps: {
                  component: CardCvcElement,
                },
              }}
            />
          </Grid>
          <Grid item xs={4} display="flex" alignItems="center">
            <LoadingButton
              loading={
                isPaymentLoading ||
                isUpdatingEnterprisePaymentPlan ||
                isBoostEnterpriseLocationLoading ||
                isBoostEnterpriseSocialLoading ||
                isBoostLocationLoading ||
                isBoostSocialLoading ||
                isUpdatingPaymentPlan ||
                isAddingPaymentMethod
              }
              variant="contained"
              type="submit"
              fullWidth
            >
              Submit
            </LoadingButton>
          </Grid>
          <Grid item xs={12}>
            {isPaymentLoading && !errorText && (
              <Paper
                sx={{
                  padding: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: '#e3f2fd',
                  color: '#1976d2',
                  borderRadius: '4px',
                  gap: '8px',
                }}
              >
                <CircularProgress
                  size={20}
                  thickness={4}
                  sx={{ marginRight: '8px' }}
                />
                <Typography>
                  Saving your credit card information now, <br />
                  almost there…
                </Typography>
              </Paper>
            )}
          </Grid>
        </Grid>
      </Stack>
    </form>
  );
};

export default PaymentInformationForm;
